import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowUp, ArrowDown, CheckCircle, XCircle } from "lucide-react";

export const TradeHistory = ({ trades }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!trades || trades.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-zinc-500" data-testid="trade-history-empty">
        <div className="text-sm mb-2">No trade history</div>
        <div className="text-xs">Closed trades will appear here</div>
      </div>
    );
  }

  const totalPnl = trades.reduce((sum, t) => sum + (t.pnl || 0), 0);
  const winCount = trades.filter((t) => (t.pnl || 0) > 0).length;
  const winRate = trades.length > 0 ? ((winCount / trades.length) * 100).toFixed(1) : 0;

  return (
    <div className="h-full flex flex-col" data-testid="trade-history">
      <div className="grid grid-cols-3 gap-2 p-4 border-b border-zinc-800 bg-zinc-950">
        <div className="text-center">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total P&L</div>
          <div className={`font-mono text-sm font-semibold ${totalPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Trades</div>
          <div className="font-mono text-sm font-semibold">{trades.length}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Win Rate</div>
          <div className="font-mono text-sm font-semibold">{winRate}%</div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {trades.map((trade) => (
            <div key={trade.id} className="bg-zinc-900 rounded border border-zinc-800 p-3" data-testid={`history-trade-${trade.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {trade.direction === "long" ? <ArrowUp className="w-4 h-4 text-green-500" /> : <ArrowDown className="w-4 h-4 text-red-500" />}
                  <span className={`font-mono text-xs uppercase font-semibold ${trade.direction === "long" ? "text-green-500" : "text-red-500"}`}>{trade.direction}</span>
                  <span className="text-xs text-zinc-500">{trade.symbol}</span>
                  {trade.exit_reason && <span className="px-1.5 py-0.5 rounded text-xs bg-zinc-800 text-zinc-400">{trade.exit_reason === "stop_loss" ? "SL" : trade.exit_reason === "take_profit" ? "TP" : "Manual"}</span>}
                </div>
                <div className="flex items-center gap-1">
                  {(trade.pnl || 0) >= 0 ? <CheckCircle className="w-3 h-3 text-green-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                  <span className={`font-mono text-sm font-semibold ${(trade.pnl || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {(trade.pnl || 0) >= 0 ? '+' : ''}${(trade.pnl || 0).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-zinc-500">Entry</span><div className="font-mono font-medium">${trade.entry_price?.toFixed(2)}</div></div>
                <div><span className="text-zinc-500">Exit</span><div className="font-mono font-medium">${trade.exit_price?.toFixed(2)}</div></div>
                <div><span className="text-zinc-500">Size</span><div className="font-mono font-medium">{trade.size} BTC</div></div>
                <div><span className="text-zinc-500">Closed</span><div className="font-mono font-medium text-zinc-400">{formatDate(trade.exit_time)}</div></div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
